<template>
  <div class="legal" role="document" :aria-labelledby="uid">
    <div class="legal__head">
      <h2 :id="uid" class="legal__title">{{ title }}</h2>
      <button type="button" class="legal__close" aria-label="Close" @click="$emit('close')">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
      </button>
    </div>

    <div ref="body" class="legal__body">
      <div class="legal__meta">
        <p v-for="(line, i) in meta" :key="'m' + i"><template v-for="(s, j) in linkify(line)"><a v-if="s.href" :key="j" :href="s.href" target="_blank" rel="noopener">{{ s.text }}</a><template v-else>{{ s.text }}</template></template></p>
      </div>
      <template v-for="(b, i) in content">
        <ul v-if="b.t === 'ul'" :key="i" class="legal__list">
          <li v-for="(item, k) in b.items" :key="k"><template v-for="(s, j) in linkify(item)"><a v-if="s.href" :key="j" :href="s.href" target="_blank" rel="noopener">{{ s.text }}</a><template v-else>{{ s.text }}</template></template></li>
        </ul>
        <component :is="tag(b.t)" v-else :key="i" :class="'legal__' + b.t"><template v-for="(s, j) in linkify(b.text)"><a v-if="s.href" :key="j" :href="s.href" target="_blank" rel="noopener">{{ s.text }}</a><template v-else>{{ s.text }}</template></template></component>
      </template>
    </div>

    <div class="legal__foot">
      <button type="button" class="legal__back" @click="$emit('close')">Back to sign up</button>
    </div>
  </div>
</template>

<script>
// Emails and web addresses in the text become links.
const LINK = /([\w.+-]+@[\w-]+(?:\.[\w-]+)+)|((?:https?:\/\/|www\.)[^\s]+)/g

// A legal document (Terms, Privacy) from legalDocuments.js, opened from the
// sign-up popup. Blocks come pre-structured; runs of list items are grouped
// into one list here.
export default {
  props: {
    title: { type: String, required: true },
    blocks: { type: Array, required: true },
  },
  computed: {
    uid() {
      return 'legal-' + this._uid
    },
    meta() {
      return this.blocks.filter(b => b.t === 'meta').map(b => b.text)
    },
    content() {
      const out = []
      for (const b of this.blocks) {
        if (b.t === 'meta') continue
        if (b.t === 'li') {
          const last = out[out.length - 1]
          if (last && last.t === 'ul') last.items.push(b.text)
          else out.push({ t: 'ul', items: [b.text] })
        } else {
          out.push(b)
        }
      }
      return out
    },
  },
  mounted() {
    // Each opening starts at the top, not where the last reader left off.
    this.$nextTick(() => { if (this.$refs.body) this.$refs.body.scrollTop = 0 })
  },
  methods: {
    tag(t) {
      return t === 'h1' || t === 'h2' ? 'h3' : t === 'h3' ? 'h4' : 'p'
    },
    linkify(text) {
      const parts = []
      let last = 0
      for (const m of text.matchAll(LINK)) {
        let found = m[0]
        // Leave sentence punctuation after an address outside the link.
        const trail = found.match(/[.,;:)]+$/)
        if (trail) found = found.slice(0, -trail[0].length)
        if (m.index > last) parts.push({ text: text.slice(last, m.index) })
        const href = m[1] ? 'mailto:' + found : (found.startsWith('www.') ? 'https://' + found : found)
        parts.push({ text: found, href })
        last = m.index + found.length
      }
      if (last < text.length) parts.push({ text: text.slice(last) })
      return parts
    },
  },
}
</script>

<style scoped>
/* Same look as the sign-up card (LoginRegister): beige title band, white
   body, Montserrat. The dialog around it (app.css, .v-dialog.legalDialog)
   caps its height so only the body scrolls. */
.legal {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  width: 100%;
  background: #FFFFFF;
  font-family: 'Montserrat', sans-serif;
  color: #3F3A32;
  -webkit-font-smoothing: antialiased;
}
.legal__head {
  position: relative;
  flex: none;
  padding: 20px 68px 18px 28px;
  background: #F2EEE8;
}
.legal__title {
  margin: 0;
  font-size: 21px;
  font-weight: 700;
  line-height: 1.28;
  color: #560354;
}
.legal__close {
  position: absolute;
  top: 50%;
  right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 50%;
  background: #FFFFFF;
  color: #4A4239;
  cursor: pointer;
  transform: translateY(-50%);
}
.legal__close:hover {
  background: #F6EFF5;
}
.legal__body {
  flex: 1 1 auto;
  min-height: 0;
  padding: 20px 28px 24px;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  font-size: 15px;
  line-height: 1.65;
  overflow-wrap: anywhere;
}
.legal__meta {
  margin-bottom: 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid #EFEAE4;
}
.legal__meta p {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #8A8074;
}
.legal__h1 {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 800;
  letter-spacing: .02em;
  color: #560354;
}
.legal__h2 {
  margin: 24px 0 8px;
  font-size: 15px;
  font-weight: 800;
  letter-spacing: .02em;
  color: #560354;
}
.legal__h3 {
  margin: 18px 0 6px;
  font-size: 15px;
  font-weight: 700;
  color: #3F3A32;
}
.legal__p {
  margin: 0 0 12px;
}
.legal__list {
  margin: 0 0 12px;
  padding-left: 22px;
  list-style: disc;
}
.legal__list li {
  margin-bottom: 6px;
  padding-left: 2px;
}
.legal__list li::marker {
  color: #560354;
}
.legal a {
  color: #560354;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.legal__foot {
  display: flex;
  flex: none;
  justify-content: center;
  padding: 14px 28px;
  border-top: 1px solid #EFEAE4;
}
.legal__back {
  min-width: 220px;
  height: 46px;
  padding: 0 28px;
  border: 0;
  border-radius: 999px;
  background: #560354;
  font: 700 16px 'Montserrat', sans-serif;
  color: #FFFFFF;
  cursor: pointer;
  transition: background-color .15s;
}
.legal__back:hover {
  background: #42003F;
}
.legal button:focus-visible,
.legal a:focus-visible {
  outline: 3px solid #C99BC7;
  outline-offset: 2px;
}

/* Phones: the dialog is full screen, so tighter sides and a full-width button. */
@media (max-width: 600px) {
  .legal__head {
    padding: 16px 64px 14px 20px;
  }
  .legal__title {
    font-size: 19px;
  }
  .legal__close {
    right: 12px;
  }
  .legal__body {
    padding: 16px 20px 20px;
    font-size: 15px;
  }
  .legal__foot {
    padding: 12px 20px calc(12px + env(safe-area-inset-bottom));
  }
  .legal__back {
    width: 100%;
  }
}
</style>
